import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ArrowLeft, X, Rocket, Settings, Users, FileText, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';

const ONBOARDING_KEY = 'xyron_onboarding_completed';

const OnboardingTour = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { language } = useLanguage();
  const [active, setActive] = useState(false);
  const [step, setStep] = useState(0);

  const steps = [
    {
      id: 'welcome',
      path: '/app/dashboard',
      labels: {
        fr: { title: 'Bienvenue sur Xyron Core ERP !', desc: 'Votre ERP suisse tout-en-un. Suivez ce guide rapide pour configurer votre entreprise et creer votre premiere facture.', btn: 'Commencer' },
        de: { title: 'Willkommen bei Xyron Core ERP!', desc: 'Ihr Schweizer All-in-One-ERP. Folgen Sie dieser Kurzanleitung, um Ihr Unternehmen einzurichten und Ihre erste Rechnung zu erstellen.', btn: 'Starten' },
        en: { title: 'Welcome to Xyron Core ERP!', desc: 'Your Swiss all-in-one ERP. Follow this quick guide to set up your company and create your first invoice.', btn: 'Get started' }
      },
      icon: Rocket,
      color: 'from-blue-600 to-indigo-600',
      position: 'center'
    },
    {
      id: 'settings',
      path: '/app/settings',
      target: '[data-testid="nav-app/settings"]',
      labels: {
        fr: { title: 'Configurez votre entreprise', desc: 'Ajoutez le nom, l\'adresse, le logo et l\'IBAN de votre entreprise. Ces informations apparaitront sur vos factures et activeront la QR-facture suisse.', btn: 'Suivant' },
        de: { title: 'Richten Sie Ihr Unternehmen ein', desc: 'Fuegen Sie Name, Adresse, Logo und IBAN Ihres Unternehmens hinzu. Diese Informationen erscheinen auf Ihren Rechnungen.', btn: 'Weiter' },
        en: { title: 'Set up your company', desc: 'Add your company name, address, logo, and IBAN. This info will appear on your invoices and enable Swiss QR-bill.', btn: 'Next' }
      },
      icon: Settings,
      color: 'from-amber-500 to-orange-500',
      position: 'right'
    },
    {
      id: 'clients',
      path: '/app/clients',
      target: '[data-testid="nav-app/clients"]',
      labels: {
        fr: { title: 'Ajoutez vos clients', desc: 'Creez votre carnet de clients. Vous pouvez aussi importer un fichier CSV si vous avez deja une liste. Chaque facture sera liee a un client.', btn: 'Suivant' },
        de: { title: 'Fuegen Sie Ihre Kunden hinzu', desc: 'Erstellen Sie Ihr Kundenverzeichnis. Sie koennen auch eine CSV-Datei importieren. Jede Rechnung wird einem Kunden zugeordnet.', btn: 'Weiter' },
        en: { title: 'Add your clients', desc: 'Build your client directory. You can also import a CSV file. Each invoice will be linked to a client.', btn: 'Next' }
      },
      icon: Users,
      color: 'from-purple-500 to-violet-600',
      position: 'right'
    },
    {
      id: 'invoice',
      path: '/app/invoices',
      target: '[data-testid="create-invoice-btn"]',
      labels: {
        fr: { title: 'Creez votre premiere facture', desc: 'Cliquez sur "Nouvelle facture", selectionnez un client, ajoutez vos articles et enregistrez. Le PDF avec QR-facture sera genere automatiquement !', btn: 'Suivant' },
        de: { title: 'Erstellen Sie Ihre erste Rechnung', desc: 'Klicken Sie auf "Neue Rechnung", waehlen Sie einen Kunden, fuegen Sie Artikel hinzu und speichern Sie. Das PDF wird automatisch generiert!', btn: 'Weiter' },
        en: { title: 'Create your first invoice', desc: 'Click "New invoice", select a client, add your items and save. The PDF with Swiss QR-bill will be generated automatically!', btn: 'Next' }
      },
      icon: FileText,
      color: 'from-blue-500 to-cyan-500',
      position: 'right'
    },
    {
      id: 'done',
      path: null,
      labels: {
        fr: { title: 'Vous etes pret !', desc: 'Votre ERP est configure. Explorez les modules RH, la comptabilite, les devis et bien plus. Utilisez le chatbot en bas a droite si vous avez des questions.', btn: 'Terminer' },
        de: { title: 'Sie sind bereit!', desc: 'Ihr ERP ist eingerichtet. Entdecken Sie die HR-Module, Buchhaltung, Offerten und mehr. Nutzen Sie den Chatbot unten rechts bei Fragen.', btn: 'Fertig' },
        en: { title: 'You\'re all set!', desc: 'Your ERP is configured. Explore the HR modules, accounting, quotes and more. Use the chatbot at the bottom-right if you have questions.', btn: 'Finish' }
      },
      icon: CheckCircle,
      color: 'from-emerald-500 to-green-600',
      position: 'center'
    }
  ];

  // Check if onboarding should start
  useEffect(() => {
    const completed = localStorage.getItem(ONBOARDING_KEY);
    if (!completed && location.pathname.startsWith('/app/')) {
      const timer = setTimeout(() => setActive(true), 1500);
      return () => clearTimeout(timer);
    }
  }, [location.pathname]);

  // Listen for manual trigger
  useEffect(() => {
    const handler = () => { setStep(0); setActive(true); };
    window.addEventListener('start-onboarding', handler);
    return () => window.removeEventListener('start-onboarding', handler);
  }, []);

  const currentStep = steps[step];
  const l = currentStep?.labels[language] || currentStep?.labels.fr;

  const goNext = useCallback(() => {
    if (step < steps.length - 1) {
      const nextStep = steps[step + 1];
      if (nextStep.path) navigate(nextStep.path);
      setStep(s => s + 1);
    } else {
      localStorage.setItem(ONBOARDING_KEY, 'true');
      setActive(false);
    }
  }, [step, navigate, steps]);

  const goBack = useCallback(() => {
    if (step > 0) {
      const prevStep = steps[step - 1];
      if (prevStep.path) navigate(prevStep.path);
      setStep(s => s - 1);
    }
  }, [step, navigate, steps]);

  const handleSkip = () => {
    localStorage.setItem(ONBOARDING_KEY, 'true');
    setActive(false);
  };

  if (!active) return null;

  const Icon = currentStep.icon;
  const isCenter = currentStep.position === 'center';

  return (
    <AnimatePresence>
      <motion.div
        key="onboarding-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm"
        data-testid="onboarding-overlay"
      >
        <motion.div
          key={`step-${step}`}
          initial={{ opacity: 0, y: 20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -20, scale: 0.95 }}
          transition={{ duration: 0.3 }}
          className={`absolute ${isCenter ? 'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2' : 'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 lg:left-[55%]'}`}
          data-testid="onboarding-card"
        >
          <div className="w-[420px] max-w-[90vw] bg-white rounded-2xl shadow-2xl overflow-hidden">
            {/* Colored header */}
            <div className={`bg-gradient-to-r ${currentStep.color} px-6 py-5 text-white`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                    <Icon className="w-5 h-5" />
                  </div>
                  <span className="text-sm font-medium opacity-80">
                    {step + 1} / {steps.length}
                  </span>
                </div>
                <button onClick={handleSkip} className="p-1.5 rounded-lg hover:bg-white/20 transition-colors" data-testid="onboarding-skip">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <h2 className="text-xl font-bold">{l.title}</h2>
            </div>

            {/* Progress bar */}
            <div className="h-1 bg-slate-100">
              <div
                className={`h-full bg-gradient-to-r ${currentStep.color} transition-all duration-500`}
                style={{ width: `${((step + 1) / steps.length) * 100}%` }}
              />
            </div>

            {/* Content */}
            <div className="px-6 py-5">
              <p className="text-slate-600 text-sm leading-relaxed">{l.desc}</p>
            </div>

            {/* Actions */}
            <div className="px-6 pb-5 flex items-center justify-between">
              <div>
                {step > 0 ? (
                  <Button variant="ghost" size="sm" onClick={goBack} data-testid="onboarding-back">
                    <ArrowLeft className="w-4 h-4 mr-1" />
                    {language === 'de' ? 'Zurueck' : language === 'en' ? 'Back' : 'Retour'}
                  </Button>
                ) : (
                  <Button variant="ghost" size="sm" onClick={handleSkip} className="text-slate-400" data-testid="onboarding-skip-text">
                    {language === 'de' ? 'Ueberspringen' : language === 'en' ? 'Skip' : 'Passer'}
                  </Button>
                )}
              </div>
              <Button onClick={goNext} className={`bg-gradient-to-r ${currentStep.color} hover:opacity-90 text-white`} data-testid="onboarding-next">
                {l.btn}
                {step < steps.length - 1 && <ArrowRight className="w-4 h-4 ml-1" />}
              </Button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export const startOnboarding = () => {
  localStorage.removeItem(ONBOARDING_KEY);
  window.dispatchEvent(new Event('start-onboarding'));
};

export default OnboardingTour;
